import React from "react";
import { MainLayout } from "components/layout/MainLayout";
import {
  Card,
  CardHeader,
  CardBody,
  Grid,
  GridItem,
  Button,
  Spinner,
  LoadingWrapper,
} from "components/ui";
import {
  ApartmentOutlined,
  GroupAddOutlined,
  BusinessOutlined,
  EmailOutlined,
  PhoneOutlined,
  LanguageOutlined,
  PlaceOutlined,
  WorkspacePremiumOutlined,
  EventOutlined,
  GroupOutlined,
  WarningOutlined,
  AssessmentOutlined,
  QrCodeScannerOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "constants/index";
import { useAuth } from "hooks/useAuth";
import { useLicenseStatus } from "hooks/useLicenseStatus";
import { appLocalStorage, appSessionStorage } from "utils/storage";
import { dashboardService } from "services/dashboard.service";
import { AdminDashboard, CompanyDashboard } from "types/dashboard";

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    companyData,
    isLoading: licenseLoading,
    error: licenseError,
  } = useLicenseStatus();

  const storedName = React.useMemo(
    () => `${user?.name || ""}` || user?.username || "User",
    [user?.name, user?.username]
  );

  const isCompanyContext = React.useMemo(
    () => Boolean(user && user.roleId && Number(user.roleId) !== 1),
    [user?.roleId]
  );

  const [loadingAdminData, setLoadingAdminData] = React.useState(false);
  const [adminData, setAdminData] = React.useState<AdminDashboard | null>(null);
  const [adminError, setAdminError] = React.useState<string | null>(null);

  // Ref to track if admin data has been loaded to prevent duplicate calls
  const adminDataLoadedRef = React.useRef(false);

  React.useEffect(() => {
    if (isCompanyContext || adminDataLoadedRef.current) return;

    let cancelled = false;
    setLoadingAdminData(true);
    setAdminError(null);

    const load = async () => {
      try {
        const data = await dashboardService.getAdminDashboard();
        if (cancelled) return;
        setAdminData(data);
        adminDataLoadedRef.current = true;
      } catch (err: any) {
        if (!cancelled)
          setAdminError(err?.message || "Failed to load admin dashboard data");
      } finally {
        if (!cancelled) setLoadingAdminData(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isCompanyContext]);

  const formatPhoneNumber = (
    phoneNumber: number | null | undefined,
    countryId: number | null | undefined
  ) => {
    if (!phoneNumber) return "Not provided";
    return `+${phoneNumber}`;
  };

  const formatWebsite = (website: string | null) => {
    if (!website) return "Not provided";
    return website.startsWith("http") ? website : `https://${website}`;
  };

  const displayValue = (
    value: string | number | null | undefined,
    placeholder: string = "Not provided"
  ) => {
    return value ? value.toString() : placeholder;
  };

  // Reset ref when user changes
  React.useEffect(() => {
    adminDataLoadedRef.current = false;
  }, [user?.id]);

  // Skeleton component for admin dashboard cards
  const AdminCardSkeleton = () => (
    <Card className="shadow-soft border border-secondary-200 hover:shadow-md transition-shadow">
      <CardHeader title="" subtitle="" />
      <CardBody>
        <div className="flex items-end gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary-200 animate-pulse"></div>
          <div className="w-16 h-8 bg-secondary-200 rounded animate-pulse"></div>
        </div>
      </CardBody>
    </Card>
  );

  if (!isCompanyContext) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <div className="bg-white border border-secondary-200 rounded-2xl shadow-soft overflow-hidden">
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-b border-secondary-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900 mb-1">
                    Welcome back, {storedName}!
                  </h1>
                  <p className="text-sm sm:text-base text-secondary-600">
                    Platform overview and insights.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {loadingAdminData ? (
            <Grid cols={2} gap="lg">
              <GridItem>
                <AdminCardSkeleton />
              </GridItem>
              <GridItem>
                <AdminCardSkeleton />
              </GridItem>
              <GridItem>
                <AdminCardSkeleton />
              </GridItem>
              <GridItem>
                <AdminCardSkeleton />
              </GridItem>
            </Grid>
          ) : (
            <Grid cols={2} gap="lg">
              <GridItem>
                <Card className="shadow-soft border border-secondary-200 hover:shadow-md transition-shadow">
                  <CardHeader title="Companies" subtitle="Total companies" />
                  <CardBody>
                    <div className="flex items-end gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                        <ApartmentOutlined fontSize="small" />
                      </div>
                      <p className="text-4xl font-bold text-secondary-900">
                        {adminData?.companiesCount ?? 0}
                      </p>
                    </div>
                  </CardBody>
                </Card>
              </GridItem>

              <GridItem>
                <Card className="shadow-soft border border-secondary-200 hover:shadow-md transition-shadow">
                  <CardHeader
                    title="Employees"
                    subtitle="Across all companies"
                  />
                  <CardBody>
                    <div className="flex items-end gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                        <GroupOutlined fontSize="small" />
                      </div>
                      <p className="text-4xl font-bold text-secondary-900">
                        {(adminData?.employeesPerCompany || []).reduce(
                          (sum, e) => sum + (e?.count || 0),
                          0
                        )}
                      </p>
                    </div>
                  </CardBody>
                </Card>
              </GridItem>

              <GridItem>
                <Card className="shadow-soft border border-secondary-200 hover:shadow-md transition-shadow">
                  <CardHeader
                    title="Active Licenses"
                    subtitle="Currently active"
                  />
                  <CardBody>
                    <div className="flex items-end gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                        <WorkspacePremiumOutlined fontSize="small" />
                      </div>
                      <p className="text-4xl font-bold text-secondary-900">
                        {adminData?.activeLicensesCount ?? 0}
                      </p>
                    </div>
                  </CardBody>
                </Card>
              </GridItem>

              <GridItem>
                <Card className="shadow-soft border border-secondary-200 hover:shadow-md transition-shadow">
                  <CardHeader
                    title="In-use Licenses"
                    subtitle="Assigned and in use"
                  />
                  <CardBody>
                    <div className="flex items-end gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                        <AssessmentOutlined fontSize="small" />
                      </div>
                      <p className="text-4xl font-bold text-secondary-900">
                        {adminData?.inUseLicensesCount ?? 0}
                      </p>
                    </div>
                  </CardBody>
                </Card>
              </GridItem>
            </Grid>
          )}

          {adminError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <span className="text-red-800 font-medium">
                  Error loading dashboard data
                </span>
              </div>
              <p className="text-red-700 text-sm mt-1">{adminError}</p>
            </div>
          )}
        </div>
      </MainLayout>
    );
  }

  // Skeleton components for company dashboard
  const CompanyHeaderSkeleton = () => (
    <div className="bg-white border border-secondary-200 rounded-2xl shadow-soft overflow-hidden">
      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 px-8 py-6 border-b border-secondary-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-secondary-200 animate-pulse"></div>
              <div>
                <div className="w-64 h-8 bg-secondary-200 rounded animate-pulse mb-2"></div>
                <div className="w-80 h-4 bg-secondary-200 rounded animate-pulse"></div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              <div className="w-24 h-8 bg-secondary-200 rounded animate-pulse"></div>
              <div className="w-32 h-8 bg-secondary-200 rounded animate-pulse"></div>
              <div className="w-28 h-8 bg-secondary-200 rounded animate-pulse"></div>
            </div>
          </div>
          <div className="w-32 h-12 bg-secondary-200 rounded animate-pulse"></div>
        </div>
      </div>
    </div>
  );

  const CompanyInfoSkeleton = () => (
    <Card className="shadow-soft border border-secondary-200">
      <CardBody className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="bg-white border border-secondary-200 rounded-xl p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-secondary-200 animate-pulse"></div>
                <div>
                  <div className="w-24 h-4 bg-secondary-200 rounded animate-pulse mb-1"></div>
                  <div className="w-20 h-3 bg-secondary-200 rounded animate-pulse"></div>
                </div>
              </div>
              <div className="w-32 h-6 bg-secondary-200 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );

  const EmployeeMetricsSkeleton = () => (
    <Grid cols={4} gap="lg">
      {Array.from({ length: 4 }).map((_, index) => (
        <GridItem key={index}>
          <Card className="hover:shadow-md transition-shadow h-full flex flex-col">
            <CardBody className="p-6 flex-1 flex flex-col">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-secondary-200 animate-pulse"></div>
                <div className="flex-1">
                  <div className="w-24 h-4 bg-secondary-200 rounded animate-pulse mb-1"></div>
                  <div className="w-20 h-3 bg-secondary-200 rounded animate-pulse"></div>
                </div>
                <div className="w-2 h-2 rounded-full bg-secondary-200 animate-pulse"></div>
              </div>
              <div className="space-y-3">
                <div className="w-16 h-8 bg-secondary-200 rounded animate-pulse"></div>
                <div className="bg-secondary-100 rounded-lg p-3">
                  <div className="w-full h-3 bg-secondary-200 rounded animate-pulse"></div>
                </div>
              </div>
            </CardBody>
          </Card>
        </GridItem>
      ))}
    </Grid>
  );

  return (
    <MainLayout>
      <div className="space-y-8">
        {licenseLoading ? (
          <CompanyHeaderSkeleton />
        ) : (
          <div className="bg-white border border-secondary-200 rounded-2xl shadow-soft overflow-hidden">
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-b border-secondary-200">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-xl bg-primary-500 flex items-center justify-center shadow-lg flex-shrink-0">
                      <BusinessOutlined
                        className="text-white text-lg sm:text-xl lg:text-2xl"
                      />
                    </div>
                    <div>
                      <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-secondary-900">
                        Welcome back, {storedName}!
                      </h1>
                      <p className="text-sm sm:text-base lg:text-lg text-secondary-600 mt-1">
                        Manage your company, team, and insights from one place.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 sm:gap-3 mt-4 sm:mt-6">
                    {!companyData?.metrics?.hasLicense ? (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-small bg-red-50 text-red-700 border border-red-200">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        <span>No Subscription</span>
                      </div>
                    ) : (
                      <>
                        <div
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-small ${
                            companyData?.metrics?.isLicenseExpired
                              ? "bg-red-50 text-red-700 border border-red-200"
                              : (companyData?.metrics?.remainingDaysToExpire ??
                                  0) <= 7
                              ? "bg-amber-50 text-amber-700 border border-amber-200"
                              : "bg-green-50 text-green-700 border border-green-200"
                          }`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${
                              companyData?.metrics?.isLicenseExpired
                                ? "bg-red-500"
                                : (companyData?.metrics
                                    ?.remainingDaysToExpire ?? 0) <= 7
                                ? "bg-amber-500"
                                : "bg-green-500"
                            }`}
                          ></div>
                          <span>
                            {companyData?.metrics?.isLicenseExpired
                              ? "Expired Subscription"
                              : (companyData?.metrics?.remainingDaysToExpire ??
                                  0) <= 7
                              ? "Subscription Expiring Soon"
                              : "Active Subscription"}
                          </span>
                        </div>
                        {companyData?.metrics?.isDemo && (
                          <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-small bg-amber-50 text-amber-700 border border-amber-200">
                            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                            <span>Demo Subscription</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-small bg-secondary-50 text-secondary-700 border border-secondary-200">
                          <GroupOutlined fontSize="small" />
                          <span>
                            {companyData?.metrics?.activeEmployees || 0} Active
                            Users
                          </span>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-small bg-primary-50 text-primary-700 border border-primary-200">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <span>
                            {companyData?.metrics?.isLicenseExpired
                              ? "Expired"
                              : `${
                                  companyData?.metrics?.remainingDaysToExpire ||
                                  0
                                } Days Left`}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {companyData?.metrics?.hasLicense ? (
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <Button
                    variant={companyData?.metrics?.isLicenseExpired ? "danger" : "warning"}
                    size="lg"
                    onClick={() => navigate(ROUTES.UPGRADE_PACKAGE)}
                    className="px-4 sm:px-6 py-2.5 sm:py-3 w-full sm:w-auto"
                    disableOnLicenseExpired={false}
                  >
                    <WarningOutlined fontSize="small" />
                    <span className="ml-2">Upgrade Package</span>
                  </Button>
                </div>
              ):(
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => navigate(ROUTES.EMPLOYEES)}
                    className="px-4 sm:px-6 py-2.5 sm:py-3 w-full sm:w-auto"
                  >
                    <GroupOutlined fontSize="small" />
                    <span className="ml-2">Manage Team</span>
                  </Button>
                </div>)}
              </div>
            </div>
          </div>
        )}

          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-secondary-900 flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary-500 flex items-center justify-center flex-shrink-0">
                    <BusinessOutlined className="text-white" fontSize="small" />
                  </div>
                  <span>Company Information</span>
                </h2>
                <p className="text-sm sm:text-base text-secondary-600 mt-1">
                  {companyData?.company?.name
                    ? `${companyData.company.name} - Complete Details`
                    : "Complete Company Details"}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (user?.company_id) {
                    navigate(`/company/${user.company_id}/profile`);
                  }
                }}
                disabled={!user?.company_id}
                className="border-primary-200 text-primary-600 hover:bg-primary-50 w-full sm:w-auto"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edit Details
              </Button>
            </div>

          {licenseLoading ? (
            <CompanyInfoSkeleton />
          ) : (
            <Card className="shadow-soft border border-secondary-200">
              <CardBody className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <div className="bg-white border border-secondary-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                        <BusinessOutlined
                          className="text-primary-600"
                          fontSize="small"
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold text-secondary-900 text-sm">
                          Company Name
                        </h3>
                        <p className="text-secondary-500 text-xs">
                          Official business name
                        </p>
                      </div>
                    </div>
                    <p className="font-small text-secondary-900 text-lg">
                      {displayValue(companyData?.company?.name, "Not set")}
                    </p>
                  </div>

                  {/* Company Summary */}
                  <div className="bg-white border border-secondary-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-secondary-50 flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-secondary-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-secondary-900 text-sm">
                          Description
                        </h3>
                        <p className="text-secondary-500 text-xs">
                          Company summary
                        </p>
                      </div>
                    </div>
                    <p className="font-small text-secondary-900 text-lg">
                      {displayValue(
                        companyData?.company?.summary,
                        "Not provided"
                      )}
                    </p>
                  </div>

                  {/* Active Employees */}
                  <div className="bg-white border border-secondary-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                        <GroupOutlined
                          className="text-green-600"
                          fontSize="small"
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold text-secondary-900 text-sm">
                          Active Employees
                        </h3>
                        <p className="text-secondary-500 text-xs">
                          Currently active
                        </p>
                      </div>
                    </div>
                    <p className="font-bold text-secondary-900 text-2xl">
                      {companyData?.company?.nbActiveEmployees || 0}
                    </p>
                  </div>

                  {/* Website */}
                  <div className="bg-white border border-secondary-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <LanguageOutlined
                          className="text-blue-600"
                          fontSize="small"
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold text-secondary-900 text-sm">
                          Website
                        </h3>
                        <p className="text-secondary-500 text-xs">
                          Company website
                        </p>
                      </div>
                    </div>
                    {companyData?.company?.website ? (
                      <a
                        href={formatWebsite(companyData.company.website)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-small text-primary-600 hover:text-primary-700 underline text-lg transition-colors"
                      >
                        {formatWebsite(companyData.company.website)}
                      </a>
                    ) : (
                      <p className="font-small text-secondary-500 text-lg">
                        Not provided
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="bg-white border border-secondary-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                        <PhoneOutlined
                          className="text-purple-600"
                          fontSize="small"
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold text-secondary-900 text-sm">
                          Phone
                        </h3>
                        <p className="text-secondary-500 text-xs">
                          Contact number
                        </p>
                      </div>
                    </div>
                    <p className="font-small text-secondary-900 text-lg">
                      {formatPhoneNumber(
                        companyData?.company?.phoneNumber,
                        companyData?.company?.phoneCountryId
                      )}
                    </p>
                  </div>

                  {/* Address */}
                  <div className="bg-white border border-secondary-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                        <PlaceOutlined
                          className="text-orange-600"
                          fontSize="small"
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold text-secondary-900 text-sm">
                          Address
                        </h3>
                        <p className="text-secondary-500 text-xs">
                          Business location
                        </p>
                      </div>
                    </div>
                    <p className="font-small text-secondary-900 text-lg">
                      {displayValue(
                        companyData?.company?.address,
                        "Not provided"
                      )}
                    </p>
                  </div>
                </div>

                {/* Social Media Links */}
                {(companyData?.company?.linkedInUrl ||
                  companyData?.company?.facebookUrl ||
                  companyData?.company?.instagramUrl ||
                  companyData?.company?.xUrl) && (
                  <div className="mt-8 pt-6 border-t border-secondary-200">
                    <h4 className="font-semibold text-secondary-900 text-lg mb-4 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-secondary-50 flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-secondary-600"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                      </div>
                      Social Media Presence
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      {companyData?.company?.linkedInUrl && (
                        <a
                          href={formatWebsite(companyData.company.linkedInUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors border border-blue-200"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                          </svg>
                          <span className="font-small">LinkedIn</span>
                        </a>
                      )}
                      {companyData?.company?.facebookUrl && (
                        <a
                          href={formatWebsite(companyData.company.facebookUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors border border-blue-200"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                          </svg>
                          <span className="font-small">Facebook</span>
                        </a>
                      )}
                      {companyData?.company?.instagramUrl && (
                        <a
                          href={formatWebsite(companyData.company.instagramUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-pink-50 hover:bg-pink-100 text-pink-700 rounded-lg transition-colors border border-pink-200"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                          </svg>
                          <span className="font-small">Instagram</span>
                        </a>
                      )}
                      {companyData?.company?.xUrl && (
                        <a
                          href={formatWebsite(companyData.company.xUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors border border-gray-200"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                          <span className="font-small">X</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Employee Metrics Section */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-secondary-900 flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary-500 flex items-center justify-center flex-shrink-0">
                    <GroupOutlined className="text-white" fontSize="small" />
                  </div>
                  <span>Employee Analytics</span>
                </h2>
                <p className="text-sm sm:text-base text-secondary-600 mt-1">
                  {!companyData?.metrics?.hasLicense
                    ? "No license available - employee metrics not accessible"
                    : "Real-time employee metrics and license utilization"}
                </p>
              </div>
            </div>

            {licenseLoading ? (
              <EmployeeMetricsSkeleton />
            ) : !companyData?.metrics?.hasLicense ? (
              <Card className="shadow-soft border border-red-200">
                <CardBody className="p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-red-800 mb-2">
                    No Subscription Available
                  </h3>
                  <p className="text-red-600 mb-4">
                    Your company does not have an active license. Employee
                    analytics and metrics are not available without a valid
                    license.
                  </p>
                  <Button
                    variant="primary"
                    size="lg"
                    className="bg-red-500 hover:bg-red-600"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    Get Subscription
                  </Button>
                </CardBody>
              </Card>
            ) : (
              <Grid cols={4} gap="lg" responsive={true}> 
                <GridItem>
                  <Card className="hover:shadow-md transition-shadow h-full flex flex-col">
                    <CardBody className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                          <WorkspacePremiumOutlined
                            className="text-green-600"
                            fontSize="small"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-secondary-900 text-sm">
                            Subscription Capacity
                          </h3>
                          <p className="text-secondary-500 text-xs">
                            Maximum allowed
                          </p>
                        </div>
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      </div>
                      <div className="space-y-3">
                        <p className="text-3xl font-bold text-secondary-900">
                          {companyData?.metrics?.maxEmployees || 0}
                        </p>
                        <div className="bg-green-50 rounded-lg p-3">
                          <p className="text-green-700 text-xs">
                            Total employee seats available in your license
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </GridItem>

                <GridItem>
                  <Card className="hover:shadow-md transition-shadow h-full flex flex-col">
                    <CardBody className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-4 mb-4">
                        <div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            companyData?.metrics?.remainingEmployees === 0
                              ? "bg-red-50"
                              : "bg-orange-50"
                          }`}
                        >
                          <GroupAddOutlined
                            className={`${
                              companyData?.metrics?.remainingEmployees === 0
                                ? "text-red-600"
                                : "text-orange-600"
                            }`}
                            fontSize="small"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-secondary-900 text-sm">
                            Remaining Seats
                          </h3>
                          <p className="text-secondary-500 text-xs">
                            {companyData?.metrics?.remainingEmployees === 0
                              ? "At capacity"
                              : "Available to add"}
                          </p>
                        </div>
                        <div
                          className={`w-2 h-2 rounded-full ${
                            companyData?.metrics?.remainingEmployees === 0
                              ? "bg-red-500"
                              : "bg-orange-500"
                          }`}
                        ></div>
                      </div>
                      <div className="space-y-3">
                        <p className="text-3xl font-bold text-secondary-900">
                          {companyData?.metrics?.remainingEmployees || 0}
                        </p>
                        <div
                          className={`rounded-lg p-3 ${
                            companyData?.metrics?.remainingEmployees === 0
                              ? "bg-red-50"
                              : "bg-orange-50"
                          }`}
                        >
                          <p
                            className={`text-xs ${
                              companyData?.metrics?.remainingEmployees === 0
                                ? "text-red-700"
                                : "text-orange-700"
                            }`}
                          >
                            {companyData?.metrics?.remainingEmployees === 0
                              ? "Subscription is at full capacity. Consider upgrading to add more employees."
                              : "You can add more employees to your team"}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </GridItem>

                <GridItem>
                  <Card className="hover:shadow-md transition-shadow h-full flex flex-col">
                    <CardBody className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-yellow-50 flex items-center justify-center">
                          <QrCodeScannerOutlined
                            className="text-yellow-600"
                            fontSize="small"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-secondary-900 text-sm">
                            Total Scans
                          </h3>
                          <p className="text-secondary-500 text-xs">
                            Employee QR code scans
                          </p>
                        </div>
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      </div>
                      <div className="space-y-3">
                        <p className="text-3xl font-bold text-secondary-900">
                          {companyData?.metrics?.totalScans || 0}
                        </p>
                        <div className="bg-yellow-50 rounded-lg p-3">
                          <p className="text-yellow-700 text-xs">
                            Total number of QR code scans by employees of this company
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </GridItem>

                <GridItem>
                  <Card className="hover:shadow-md transition-shadow h-full flex flex-col">
                    <CardBody className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-4 mb-4">
                        <div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            companyData?.metrics?.isLicenseExpired
                              ? "bg-red-50"
                              : (companyData?.metrics?.remainingDaysToExpire ??
                                  0) <= 7
                              ? "bg-amber-50"
                              : "bg-purple-50"
                          }`}
                        >
                          <svg
                            className={`w-5 h-5 ${
                              companyData?.metrics?.isLicenseExpired
                                ? "text-red-600"
                                : (companyData?.metrics
                                    ?.remainingDaysToExpire ?? 0) <= 7
                                ? "text-amber-600"
                                : "text-purple-600"
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-secondary-900 text-sm">
                            Status
                          </h3>
                          <p className="text-secondary-500 text-xs">
                            {companyData?.metrics?.isLicenseExpired
                              ? "Subscription has expired"
                              : (companyData?.metrics?.remainingDaysToExpire ??
                                  0) <= 7
                              ? "Expiring soon"
                              : "Days remaining"}
                          </p>
                        </div>
                        <div
                          className={`w-2 h-2 rounded-full ${
                            companyData?.metrics?.isLicenseExpired
                              ? "bg-red-500"
                              : (companyData?.metrics?.remainingDaysToExpire ??
                                  0) <= 7
                              ? "bg-amber-500"
                              : "bg-purple-500"
                          }`}
                        ></div>
                      </div>
                      <div className="space-y-3">
                        <p className="text-3xl font-bold text-secondary-900">
                          {companyData?.metrics?.isLicenseExpired
                            ? "0"
                            : companyData?.metrics?.remainingDaysToExpire || 0}
                        </p>
                        <div
                          className={`rounded-lg p-3 ${
                            companyData?.metrics?.isLicenseExpired
                              ? "bg-red-50"
                              : (companyData?.metrics?.remainingDaysToExpire ??
                                  0) <= 7
                              ? "bg-amber-50"
                              : "bg-purple-50"
                          }`}
                        >
                          <p
                            className={`text-xs ${
                              companyData?.metrics?.isLicenseExpired
                                ? "text-red-700"
                                : (companyData?.metrics
                                    ?.remainingDaysToExpire ?? 0) <= 7
                                ? "text-amber-700"
                                : "text-purple-700"
                            }`}
                          >
                            {companyData?.metrics?.isLicenseExpired
                              ? "Subscription has expired. Please renew to continue using the service."
                              : (companyData?.metrics?.remainingDaysToExpire ??
                                  0) <= 7
                              ? "Your license is expiring soon. Consider renewing to avoid service interruption."
                              : "Your license is active and in good standing."}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </GridItem>
              </Grid>
            )}

            {/* Subscription Status Overview */}
            {licenseLoading ? (
              // <Card className="shadow-soft border border-secondary-200">
              //   <CardBody className="p-6">
              //     <div className="space-y-6">
              //       <div className="flex items-center justify-between mb-6">
              //         <div>
              //           <div className="w-48 h-6 bg-secondary-200 rounded animate-pulse mb-2"></div>
              //           <div className="w-64 h-4 bg-secondary-200 rounded animate-pulse"></div>
              //         </div>
              //         <div className="flex items-center gap-2">
              //           <div className="w-3 h-3 rounded-full bg-secondary-200 animate-pulse"></div>
              //           <div className="w-24 h-4 bg-secondary-200 rounded animate-pulse"></div>
              //         </div>
              //       </div>
              //       <div className="space-y-4">
              //         <div className="w-full h-3 bg-secondary-200 rounded animate-pulse"></div>
              //         <div className="w-full h-16 bg-secondary-200 rounded animate-pulse"></div>
              //         <div className="w-full h-16 bg-secondary-200 rounded animate-pulse"></div>
              //       </div>
              //     </div>
              //   </CardBody>
              // </Card>
              <></>
            ) : (
              // companyData?.metrics?.hasLicense && (
              //   <Card className="shadow-soft border border-secondary-200">
              //     <CardBody className="p-6">
              //       <div className="flex items-center justify-between mb-6">
              //         <div>
              //           <h3 className="text-xl font-bold text-secondary-900 flex items-center gap-3">
              //             <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
              //               <AssessmentOutlined
              //                 className="text-white"
              //                 fontSize="small"
              //               />
              //             </div>
              //             Subscription Status Overview
              //           </h3>
              //           <p className="text-secondary-600 mt-1">
              //             Current license utilization and insights
              //           </p>
              //         </div>
              //         <div className="flex items-center gap-2">
              //           <div
              //             className={`w-3 h-3 rounded-full ${
              //               companyData?.metrics?.isLicenseExpired
              //                 ? "bg-red-500"
              //                 : (companyData?.metrics?.remainingDaysToExpire ??
              //                     0) <= 7
              //                 ? "bg-amber-500"
              //                 : "bg-green-500"
              //             }`}
              //           ></div>
              //           <span
              //             className={`text-sm font-semibold ${
              //               companyData?.metrics?.isLicenseExpired
              //                 ? "text-red-600"
              //                 : (companyData?.metrics?.remainingDaysToExpire ??
              //                     0) <= 7
              //                 ? "text-amber-600"
              //                 : "text-green-600"
              //             }`}
              //           >
              //             {companyData?.metrics?.isLicenseExpired
              //               ? "Subscription Expired"
              //               : (companyData?.metrics?.remainingDaysToExpire ??
              //                   0) <= 7
              //               ? "Expiring Soon"
              //               : "Subscription Active"}
              //           </span>
              //         </div>
              //       </div>

              //       <div className="space-y-6">
              //         {/* Progress Bar */}
              //         <div className="space-y-4">
              //           <div className="flex justify-between items-center">
              //             <span className="text-secondary-700 font-small">
              //               Subscription Utilization
              //             </span>
              //             <span className="font-bold text-secondary-900 text-lg">
              //               {companyData?.metrics?.activeEmployees || 0} /{" "}
              //               {companyData?.metrics?.maxEmployees || 0}
              //             </span>
              //           </div>
              //           <div className="relative">
              //             <div className="w-full bg-secondary-200 rounded-full h-3 overflow-hidden">
              //               <div
              //                 className={`h-3 rounded-full transition-all duration-1000 ease-out ${
              //                   companyData?.metrics?.remainingEmployees === 0
              //                     ? "bg-red-500"
              //                     : companyData?.metrics?.remainingEmployees ===
              //                       1
              //                     ? "bg-amber-500"
              //                     : "bg-green-500"
              //                 }`}
              //                 style={{
              //                   width: `${
              //                     companyData?.metrics?.maxEmployees
              //                       ? (companyData.metrics.activeEmployees /
              //                           companyData.metrics.maxEmployees) *
              //                         100
              //                       : 0
              //                   }%`,
              //                 }}
              //               ></div>
              //             </div>
              //             <div className="flex justify-between text-xs text-secondary-500 mt-2">
              //               <span>0%</span>
              //               <span>50%</span>
              //               <span>100%</span>
              //             </div>
              //           </div>
              //         </div>

              //         {/* Subscription Expiration Status */}
              //         <div
              //           className={`p-6 rounded-xl border-l-4 ${
              //             companyData?.metrics?.isLicenseExpired
              //               ? "bg-red-50 border-red-500"
              //               : (companyData?.metrics?.remainingDaysToExpire ??
              //                   0) <= 7
              //               ? "bg-amber-50 border-amber-500"
              //               : "bg-green-50 border-green-500"
              //           }`}
              //         >
              //           <div className="flex items-start gap-4">
              //             <div
              //               className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              //                 companyData?.metrics?.isLicenseExpired
              //                   ? "bg-red-500"
              //                   : (companyData?.metrics
              //                       ?.remainingDaysToExpire ?? 0) <= 7
              //                   ? "bg-amber-500"
              //                   : "bg-green-500"
              //               } text-white`}
              //             >
              //               {companyData?.metrics?.isLicenseExpired ? (
              //                 <svg
              //                   className="w-6 h-6"
              //                   fill="none"
              //                   stroke="currentColor"
              //                   viewBox="0 0 24 24"
              //                 >
              //                   <path
              //                     strokeLinecap="round"
              //                     strokeLinejoin="round"
              //                     strokeWidth={2}
              //                     d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              //                   />
              //                 </svg>
              //               ) : (companyData?.metrics?.remainingDaysToExpire ??
              //                   0) <= 7 ? (
              //                 <svg
              //                   className="w-6 h-6"
              //                   fill="none"
              //                   stroke="currentColor"
              //                   viewBox="0 0 24 24"
              //                 >
              //                   <path
              //                     strokeLinecap="round"
              //                     strokeLinejoin="round"
              //                     strokeWidth={2}
              //                     d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              //                   />
              //                 </svg>
              //               ) : (
              //                 <svg
              //                   className="w-6 h-6"
              //                   fill="none"
              //                   stroke="currentColor"
              //                   viewBox="0 0 24 24"
              //                 >
              //                   <path
              //                     strokeLinecap="round"
              //                     strokeLinejoin="round"
              //                     strokeWidth={2}
              //                     d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              //                   />
              //                 </svg>
              //               )}
              //             </div>
              //             <div className="flex-1">
              //               <h4
              //                 className={`font-bold text-lg mb-2 ${
              //                   companyData?.metrics?.isLicenseExpired
              //                     ? "text-red-800"
              //                     : (companyData?.metrics
              //                         ?.remainingDaysToExpire ?? 0) <= 7
              //                     ? "text-amber-800"
              //                     : "text-green-800"
              //                 }`}
              //               >
              //                 {companyData?.metrics?.isLicenseExpired
              //                   ? "Subscription Expired"
              //                   : (companyData?.metrics
              //                       ?.remainingDaysToExpire ?? 0) <= 7
              //                   ? "Subscription Expiring Soon"
              //                   : "Subscription Active"}
              //               </h4>
              //               <p
              //                 className={`text-sm ${
              //                   companyData?.metrics?.isLicenseExpired
              //                     ? "text-red-700"
              //                     : (companyData?.metrics
              //                         ?.remainingDaysToExpire ?? 0) <= 7
              //                     ? "text-amber-700"
              //                     : "text-green-700"
              //                 }`}
              //               >
              //                 {companyData?.metrics?.isLicenseExpired
              //                   ? "Your license has expired. Please renew immediately to continue using the service without interruption."
              //                   : (companyData?.metrics
              //                       ?.remainingDaysToExpire ?? 0) <= 7
              //                   ? `Your license expires in ${companyData?.metrics?.remainingDaysToExpire} days. Consider renewing soon to avoid service interruption.`
              //                   : `Your license is active and expires in ${companyData?.metrics?.remainingDaysToExpire} days. You're in good standing.`}
              //               </p>
              //               {(companyData?.metrics?.isLicenseExpired ||
              //                 (companyData?.metrics?.remainingDaysToExpire ??
              //                   0) <= 7) && (
              //                 <div className="mt-4">
              //                   <Button
              //                     variant="primary"
              //                     size="sm"
              //                     className={`${
              //                       companyData?.metrics?.isLicenseExpired
              //                         ? "bg-red-500 hover:bg-red-600"
              //                         : "bg-amber-500 hover:bg-amber-600"
              //                     }`}
              //                   >
              //                     <svg
              //                       className="w-4 h-4 mr-2"
              //                       fill="none"
              //                       stroke="currentColor"
              //                       viewBox="0 0 24 24"
              //                     >
              //                       <path
              //                         strokeLinecap="round"
              //                         strokeLinejoin="round"
              //                         strokeWidth={2}
              //                         d="M13 10V3L4 14h7v7l9-11h-7z"
              //                       />
              //                     </svg>
              //                     {companyData?.metrics?.isLicenseExpired
              //                       ? "Renew Subscription"
              //                       : "Renew Subscription"}
              //                   </Button>
              //                 </div>
              //               )}
              //             </div>
              //           </div>
              //         </div>

              //         {/* Employee Capacity Status */}
              //         <div
              //           className={`p-6 rounded-xl border-l-4 ${
              //             companyData?.metrics?.remainingEmployees === 0
              //               ? "bg-red-50 border-red-500"
              //               : companyData?.metrics?.remainingEmployees === 1
              //               ? "bg-amber-50 border-amber-500"
              //               : "bg-green-50 border-green-500"
              //           }`}
              //         >
              //           <div className="flex items-start gap-4">
              //             <div
              //               className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              //                 companyData?.metrics?.remainingEmployees === 0
              //                   ? "bg-red-500"
              //                   : companyData?.metrics?.remainingEmployees === 1
              //                   ? "bg-amber-500"
              //                   : "bg-green-500"
              //               } text-white`}
              //             >
              //               {companyData?.metrics?.remainingEmployees === 0 ? (
              //                 <svg
              //                   className="w-6 h-6"
              //                   fill="none"
              //                   stroke="currentColor"
              //                   viewBox="0 0 24 24"
              //                 >
              //                   <path
              //                     strokeLinecap="round"
              //                     strokeLinejoin="round"
              //                     strokeWidth={2}
              //                     d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              //                   />
              //                 </svg>
              //               ) : companyData?.metrics?.remainingEmployees ===
              //                 1 ? (
              //                 <svg
              //                   className="w-6 h-6"
              //                   fill="none"
              //                   stroke="currentColor"
              //                   viewBox="0 0 24 24"
              //                 >
              //                   <path
              //                     strokeLinecap="round"
              //                     strokeLinejoin="round"
              //                     strokeWidth={2}
              //                     d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              //                   />
              //                 </svg>
              //               ) : (
              //                 <svg
              //                   className="w-6 h-6"
              //                   fill="none"
              //                   stroke="currentColor"
              //                   viewBox="0 0 24 24"
              //                 >
              //                   <path
              //                     strokeLinecap="round"
              //                     strokeLinejoin="round"
              //                     strokeWidth={2}
              //                     d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              //                   />
              //                 </svg>
              //               )}
              //             </div>
              //             <div className="flex-1">
              //               <h4
              //                 className={`font-bold text-lg mb-2 ${
              //                   companyData?.metrics?.remainingEmployees === 0
              //                     ? "text-red-800"
              //                     : companyData?.metrics?.remainingEmployees ===
              //                       1
              //                     ? "text-amber-800"
              //                     : "text-green-800"
              //                 }`}
              //               >
              //                 {companyData?.metrics?.remainingEmployees === 0
              //                   ? "Subscription at Full Capacity"
              //                   : companyData?.metrics?.remainingEmployees === 1
              //                   ? "Almost at Capacity"
              //                   : "Subscription Utilization Healthy"}
              //               </h4>
              //               <p
              //                 className={`text-sm ${
              //                   companyData?.metrics?.remainingEmployees === 0
              //                     ? "text-red-700"
              //                     : companyData?.metrics?.remainingEmployees ===
              //                       1
              //                     ? "text-amber-700"
              //                     : "text-green-700"
              //                 }`}
              //               >
              //                 {companyData?.metrics?.remainingEmployees === 0
              //                   ? "Your license has reached its maximum capacity. Consider upgrading to add more employees and continue growing your team."
              //                   : companyData?.metrics?.remainingEmployees === 1
              //                   ? "You have only 1 seat remaining. Consider upgrading your license soon to avoid capacity issues."
              //                   : `You have ${companyData?.metrics?.remainingEmployees} seats available. Your license utilization is healthy.`}
              //               </p>
              //               {companyData?.metrics?.remainingEmployees === 0 && (
              //                 <div className="mt-4">
              //                   <Button
              //                     variant="primary"
              //                     size="sm"
              //                     className="bg-red-500 hover:bg-red-600"
              //                   >
              //                     <svg
              //                       className="w-4 h-4 mr-2"
              //                       fill="none"
              //                       stroke="currentColor"
              //                       viewBox="0 0 24 24"
              //                     >
              //                       <path
              //                         strokeLinecap="round"
              //                         strokeLinejoin="round"
              //                         strokeWidth={2}
              //                         d="M13 10V3L4 14h7v7l9-11h-7z"
              //                       />
              //                     </svg>
              //                     Upgrade Subscription
              //                   </Button>
              //                 </div>
              //               )}
              //             </div>
              //           </div>
              //         </div>
              //       </div>
              //     </CardBody>
              //   </Card>
              // )
              <></>
            )}
          </div>

          {/* Company Dashboard Error Handling */}
          {licenseError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <span className="text-red-800 font-medium">
                  Error loading company dashboard
                </span>
              </div>
              <p className="text-red-700 text-sm mt-1">{licenseError}</p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};
